import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// ============================================================
// Un campo de texto que, al tocarlo, despliega la lista para elegir.
//
// El planificador usaba la lista nativa del navegador (datalist): al
// tocar un campo que ya tenía un ejercicio, solo mostraba las opciones
// que coincidían con lo escrito —o sea, ninguna otra— y había que
// borrar y escribir a mano para cambiarlo.
//
// Acá, al tocarlo aparece la lista entera. Escribir la filtra (sin
// importar tildes ni mayúsculas, palabra por palabra: "remo manc"
// encuentra "Remo con mancuerna"). Con las flechas y Enter se elige
// sin el mouse. Lo escrito vale igual aunque no esté en la lista.
//
// La lista se dibuja fuera de la planilla: adentro quedaba recortada
// por el recuadro que se desliza de costado.
// ============================================================

const normal = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// opciones: ["a", "b"] o [{ label: "Grupo", items: ["a", "b"] }]
export default function ElegirOpcion({ value, onChange, opciones = [], className = "", placeholder = "" }) {
  const campo = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [marcado, setMarcado] = useState(0);
  const [pos, setPos] = useState(null);

  const grupos = (opciones.length && typeof opciones[0] === "object")
    ? opciones : [{ label: null, items: opciones }];
  const palabras = normal(filtro).split(/\s+/).filter(Boolean);
  const visibles = grupos
    .map((g) => ({ ...g, items: (g.items || []).filter((it) => {
      const n = normal(it);
      return palabras.every((p) => n.includes(p));
    }) }))
    .filter((g) => g.items.length);
  const planos = visibles.flatMap((g) => g.items);

  const ubicar = () => {
    const r = campo.current?.getBoundingClientRect();
    if (!r) return;
    const abajo = window.innerHeight - r.bottom;
    const alto = Math.min(300, Math.max(abajo, r.top) - 12);
    setPos({
      left: Math.min(r.left, window.innerWidth - Math.max(r.width, 320) - 8),
      width: Math.max(r.width, 320),
      ...(abajo >= 200 || abajo >= r.top
        ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }),
      alto,
    });
  };

  useLayoutEffect(() => { if (abierto) ubicar(); }, [abierto]);
  useEffect(() => {
    if (!abierto) return;
    const mover = () => ubicar();
    window.addEventListener("scroll", mover, true);
    window.addEventListener("resize", mover);
    return () => { window.removeEventListener("scroll", mover, true); window.removeEventListener("resize", mover); };
  }, [abierto]);

  const elegir = (v) => { onChange(v); setAbierto(false); setFiltro(""); campo.current?.blur(); };

  return (
    <>
      <input ref={campo} value={value || ""} placeholder={placeholder} className={className}
        onFocus={(e) => { setFiltro(""); setMarcado(0); setAbierto(true); e.target.select(); }}
        onChange={(e) => { onChange(e.target.value); setFiltro(e.target.value); setMarcado(0); setAbierto(true); }}
        onBlur={() => setAbierto(false)}
        onKeyDown={(e) => {
          if (!abierto) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setMarcado((m) => Math.min(m + 1, planos.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setMarcado((m) => Math.max(m - 1, 0)); }
          else if (e.key === "Enter") { e.preventDefault(); if (filtro && planos[marcado]) elegir(planos[marcado]); else { setAbierto(false); campo.current?.blur(); } }
          else if (e.key === "Escape") { setAbierto(false); campo.current?.blur(); }
        }} />
      {abierto && pos && planos.length > 0 && ReactDOM.createPortal(
        <div data-elegir-opcion
          className="fixed z-[80] rounded-lg border border-neutral-600 bg-neutral-800 shadow-xl shadow-black/50 overflow-y-auto py-1 text-sm"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.alto,
                   overscrollBehavior: "contain" }}
          // Sin esto, tocar la lista le saca el foco al campo y se cierra
          // antes de registrar la elección.
          onMouseDown={(e) => e.preventDefault()}>
          {visibles.map((g, gi) => (
            <div key={g.label || gi}>
              {g.label && (
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{g.label}</p>
              )}
              {g.items.map((it) => {
                const k = planos.indexOf(it);
                const actual = normal(it) === normal(value);
                return (
                  <button key={it} type="button" onClick={() => elegir(it)}
                    ref={(el) => { if (el && k === marcado && filtro) el.scrollIntoView({ block: "nearest" }); }}
                    className={`w-full text-left px-3 py-1.5 truncate ${
                      k === marcado && filtro ? "bg-sky-400/20 text-white"
                      : actual ? "text-sky-400" : "text-neutral-200 hover:bg-neutral-700"}`}>
                    {it}
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body)}
    </>
  );
}
